import { DateTime } from 'luxon';
import { connect, ConnectedProps } from 'react-redux';

import { formatBillingDate } from 'dashboard/lib/utils/formatters';
import { Commitment, Invoice } from 'dashboard/models/invoice';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import CommitmentsTable from 'dashboard/views/DashboardOrganizationInvoicePendingView/CommitmentsTable';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useProductFeature from 'lib/hooks/useProductFeature';

import InvoicePendingLineItemsTable from './InvoicePendingLineItemsTable';

export interface DashboardOrganizationInvoicePendingViewProps {
  invoice: Invoice | undefined;
  commitments: Commitment[];
}

const DashboardOrganizationInvoicePendingView = ({
  invoice,
  commitments,
}: DashboardOrganizationInvoicePendingViewProps & PropsFromRedux) => {
  // Technically they shouldn't see the tab altogether unless this was enabled. This is mostly just to prevent directly
  // routing to the url path.
  const currentInvoiceProductFeatureEnabled = useProductFeature(
    ProductFeatureName.BillingDashboardCurrentInvoice,
  );

  const showCommitments = useFeatureFlag('dashboard:billing-dashboard-show-commitments');

  if (currentInvoiceProductFeatureEnabled && invoice) {
    return (
      <>
        <Card>
          <Card.Body>
            <Spacer height={2} />
            <Card.Title>
              {`${formatBillingDate(invoice.attributes['period-start-at'])} - ${formatBillingDate(
                invoice.attributes['period-end-at'],
              )}`}
            </Card.Title>
            {invoice.attributes['source-data-updated-as-of'] && (
              <>
                <Paragraph>
                  Usage data is updated through{' '}
                  {DateTime.fromISO(invoice.attributes['source-data-updated-as-of']).toFormat(
                    'LLL d, y hh:mm a',
                  )}
                </Paragraph>
              </>
            )}
            {invoice && <InvoicePendingLineItemsTable invoice={invoice} />}
          </Card.Body>
        </Card>
        {showCommitments && commitments.length > 0 && (
          <>
            <Card>
              <Card.Body>
                <Spacer height={2} />
                <Card.Title>Commitments</Card.Title>
                <Paragraph>
                  Your plan includes a minimum commitment. For more details on minimum commitments
                  and discounts, refer to your order form.
                </Paragraph>
                <CommitmentsTable commitments={commitments} />
              </Card.Body>
            </Card>
          </>
        )}
      </>
    );
  } else {
    return (
      <Card>
        <Card.Body>
          <Paragraph>
            We&rsquo;re putting the finishing touches on your usage data. We&rsquo;ll populate this
            tab as soon as it&rsquo;s ready.
          </Paragraph>
        </Card.Body>
      </Card>
    );
  }
};

const mapDispatchToProps = {
  showMessage,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardOrganizationInvoicePendingView);
